import { create } from 'zustand';
import { liveQuery } from 'dexie';
import { db } from '../db/index.js';
import { settingsId } from '../db/ids.js';
import { getMeta, setMeta } from '../db/meta.js';
import { applyLocal } from '../db/mutate.js';
import { useSettings } from '../app/settingsStore.js';
import { KINDS } from './schema.js';

const JOIN_KEY = 'settingsJoin';
const KEYS = KINDS.settings.keys;
const NESTED = new Set(KINDS.settings.nested);

/** `shared` follows the account. `device` keeps this install's own settings. `ask` is the prompt. */
export const useSettingsJoin = create(() => ({ mode: null, open: false }));

let activeUser = null;
let syncMode = null;
let applying = false;
let watchSub = null;
let installed = false;

export function changedSettings(prev, next) {
  const patch = {};
  for (const key of KEYS) {
    if (NESTED.has(key)) {
      const a = prev?.[key] ?? {};
      const b = next?.[key] ?? {};
      const sub = {};
      for (const name of new Set([...Object.keys(a), ...Object.keys(b)])) {
        if (!Object.is(a[name], b[name])) sub[name] = b[name] ?? null;
      }
      if (Object.keys(sub).length) patch[key] = sub;
    } else if (!Object.is(prev?.[key], next?.[key])) {
      patch[key] = next[key];
    }
  }
  return patch;
}

function rowHasAny(row) {
  return !!row && Object.keys(row._hlc ?? {}).length > 0;
}

function presentSubs(row, key) {
  const prefix = `${key}.`;
  return new Set(
    Object.keys(row?._hlc ?? {})
      .filter((k) => k.startsWith(prefix))
      .map((k) => k.slice(prefix.length)),
  );
}

/** Remote fields replace local ones. Directions of `swipe` merge one by one. */
export function overlayShared(local, row) {
  const next = { ...local };
  if (!rowHasAny(row)) return next;
  for (const key of KEYS) {
    if (NESTED.has(key)) {
      const subs = presentSubs(row, key);
      if (!subs.size) continue;
      const merged = { ...(local[key] ?? {}) };
      for (const name of subs) merged[name] = row[key]?.[name] ?? null;
      next[key] = merged;
    } else if (row._hlc?.[key]) {
      next[key] = row[key];
    }
  }
  return next;
}

function missingFromRow(local, row) {
  const missing = {};
  for (const key of KEYS) {
    if (NESTED.has(key)) {
      const have = presentSubs(row, key);
      const sub = {};
      for (const [name, value] of Object.entries(local[key] ?? {})) {
        if (!have.has(name)) sub[name] = value;
      }
      if (Object.keys(sub).length) missing[key] = sub;
    } else if (!row?._hlc?.[key]) {
      missing[key] = local[key];
    }
  }
  return missing;
}

function applySettingsPatch(patch) {
  if (!Object.keys(patch).length) return;
  const state = useSettings.getState();
  const next = { ...patch };
  for (const key of Object.keys(patch)) {
    if (NESTED.has(key)) next[key] = { ...(state[key] ?? {}), ...patch[key] };
  }
  applying = true;
  try {
    state.set(next);
  } finally {
    applying = false;
  }
}

export function localSettingValues(state = useSettings.getState()) {
  const values = {};
  for (const key of KEYS) values[key] = state[key];
  return values;
}

/**
 * Join the account's settings document.
 * Fields the cloud already has win. Fields only this device knows are uploaded.
 */
export async function adoptShared(database, userId, local) {
  const id = settingsId(userId);
  const row = await database.settings.get(id);
  const next = overlayShared(local, row);
  await setMeta(database, JOIN_KEY, 'shared');
  const missing = missingFromRow(local, row);
  if (Object.keys(missing).length) await applyLocal(database, 'settings', id, missing);
  return next;
}

/** Stop publishing. The shared document is left as it is for other devices. */
export async function keepDeviceSettings(database) {
  await setMeta(database, JOIN_KEY, 'device');
}

/**
 * After a successful sync. The first device to find an empty document becomes
 * its source. A device that finds an existing document is asked before joining.
 */
export async function settleSettings(database, userId, local) {
  const mode = await getMeta(database, JOIN_KEY);
  if (mode === 'device' || mode === 'shared') return { action: mode };
  const row = await database.settings.get(settingsId(userId));
  if (rowHasAny(row)) return { action: 'prompt' };
  await adoptShared(database, userId, local);
  return { action: 'seeded' };
}

function applyJoinResult(result) {
  if (result.action === 'prompt') {
    syncMode = 'ask';
    useSettingsJoin.setState({ mode: 'ask', open: true });
    return;
  }
  syncMode = result.action === 'seeded' ? 'shared' : result.action;
  useSettingsJoin.setState({ mode: syncMode, open: false });
}

function watchRow(userId) {
  watchSub?.unsubscribe();
  const id = settingsId(userId);
  watchSub = liveQuery(() => db.settings.get(id)).subscribe({
    next: (row) => {
      if (syncMode !== 'shared' || !rowHasAny(row)) return;
      applySettingsPatch(changedSettings(useSettings.getState(), overlayShared(localSettingValues(), row)));
    },
    error: () => {},
  });
}

/** Subscribe once. Publishing starts only after this device has joined. */
export function installSettingsBridge() {
  if (installed) return;
  installed = true;
  useSettings.subscribe((state, prev) => {
    if (applying || syncMode !== 'shared' || !activeUser) return;
    const patch = changedSettings(prev, state);
    if (!Object.keys(patch).length) return;
    const userId = activeUser;
    void applyLocal(db, 'settings', settingsId(userId), patch);
  });
}

export async function activateSettingsUser(userId) {
  activeUser = userId;
  watchSub?.unsubscribe();
  watchSub = null;
  if (!userId) {
    syncMode = null;
    useSettingsJoin.setState({ mode: null, open: false });
    return;
  }
  syncMode = (await getMeta(db, JOIN_KEY)) ?? null;
  useSettingsJoin.setState({ mode: syncMode, open: false });
  watchRow(userId);
}

export async function settleActiveSettings(userId) {
  if (!userId || userId !== activeUser) return null;
  const result = await settleSettings(db, userId, localSettingValues());
  applyJoinResult(result);
  return result;
}

export async function chooseSharedSettings() {
  if (!activeUser) return;
  const next = await adoptShared(db, activeUser, localSettingValues());
  syncMode = 'shared';
  useSettingsJoin.setState({ mode: 'shared', open: false });
  applySettingsPatch(changedSettings(useSettings.getState(), next));
}

export async function chooseDeviceSettings() {
  await keepDeviceSettings(db);
  syncMode = 'device';
  useSettingsJoin.setState({ mode: 'device', open: false });
}
